import { isStreamRunning } from '@/utils/bapi-functions';

// Stream routing page: compares the plan, the encoder stream stats and the encoder output URLs, all from B-API's
// memory (GET /calendar/state). Pure functions only - the page does the loading and polling.

export const OUTPUT_DOMAIN = 'procricket.cloud';

const norm = v => String(v ?? '').trim();
const lower = v => norm(v).toLowerCase();
const same = (a, b) => lower(a) === lower(b);

export const streamKey = (encoder, streamId) => lower(encoder) + '|' + norm(streamId);

export function expectedOutputUrl(ch) {
    return `rtmp://${norm(ch.origin)}.${OUTPUT_DOMAIN}/${norm(ch.streamApp)}/${norm(ch.channelName)}`;
}

// rtmp://eu-origin1.procricket.cloud/procricket/stream_1?key=... -> origin, app, channel
export function parseRoute(url) {
    if (typeof url !== 'string' || !url.trim()) return null;
    try {
        const u = new URL(url.trim());
        const host = u.hostname.split('.');
        const path = u.pathname.split('/').filter(Boolean);
        if (!host[0] || !path.length) return null;
        return {
            scheme: u.protocol + '//',
            origin: host[0],
            hostRest: host.length > 1 ? '.' + host.slice(1).join('.') : '',
            app: path.length > 1 ? path[0] : '',
            channel: path[path.length - 1],
            query: u.search
        };
    } catch (e) {
        return null;
    }
}

const maskQuery = q => (q || '').replace(/(key=)[^&]*/i, '$1••••••');

// URL split into parts so the differing ones can be highlighted.
export function urlSegments(route, diff) {
    if (!route) return [];
    return [
        { t: route.scheme || 'rtmp://', diff: false },
        { t: route.origin, diff: !!diff.origin },
        { t: (route.hostRest || '.' + OUTPUT_DOMAIN) + '/', diff: false },
        { t: route.app, diff: !!diff.app },
        { t: '/', diff: false },
        { t: route.channel, diff: !!diff.channel },
        { t: maskQuery(route.query), diff: false }
    ].filter(s => s.t);
}

// The plan from B-API's records: every active feed whose stream window (match start - lead .. match end + trail) is
// open now - the feeds B-API keeps running. Grouped by match; feeds with an encoder and stream id are the channels.
export function bapiPlan(state, nowMs) {
    const byMatch = new Map();
    const channels = [];
    for (const f of (state && state.feeds) || []) {
        const c = f.cal;
        if (!c.active || nowMs < Date.parse(c.streamStartTime) || nowMs >= Date.parse(c.streamEndTime)) continue;
        const id = String(c.calMatchId);
        if (!byMatch.has(id)) {
            byMatch.set(id, {
                id,
                title: String(c.match || '').replace(' v ', ' vs '),
                start: Date.parse(c.matchStartTime),
                end: Date.parse(c.matchEndTime),
                channelCount: 0,
                unlinked: 0
            });
        }
        const match = byMatch.get(id);
        match.channelCount++;
        if (!norm(c.encoder) || !norm(c.encStreamId)) {
            match.unlinked++;
            continue;
        }
        channels.push({
            calChannelId: c.calChannelId,
            encoder: c.encoder,
            encStreamId: c.encStreamId,
            encStreamName: c.encStreamName,
            origin: c.origin,
            streamApp: c.appPath,
            channelName: c.channelName,
            match
        });
    }
    const matches = [...byMatch.values()].sort((x, y) => x.start - y.start);
    return { matches, channels };
}

// The output URL of each feed's stream, as B-API last read it (start, take-over, restart, or every tick while it runs
// with a wrong URL). Key = streamKey(encoder, stream id). A feed B-API has not read yet has no entry ('checking').
export function bapiOutputs(state) {
    const out = {};
    for (const f of (state && state.feeds) || []) {
        if (!f.urlsReadAt) continue;
        const key = streamKey(f.cal.encoder, f.cal.encStreamId);
        const at = new Date(f.urlsReadAt);
        if (out[key] && out[key].at >= at) continue;          // two feeds on one stream: the newest read
        const url = (f.outputUrls || [])[0] || null;
        out[key] = { url, route: parseRoute(url), at };
    }
    return out;
}

export const ERROR_KINDS = ['mismatch', 'nooutput'];

/*
 * One row per encoder stream used by a live match: whether it runs and where its output goes.
 * kind:
 *   live       running, output matches the calendar
 *   checking   running, output not read yet / read failed
 *   mismatch   running, output origin (err 'origin') or app/channel (err 'route') differs
 *   nooutput   running, encoder stream has no output URL
 *   down       not running
 *   unknown    encoder status not available
 */
export function buildRoutes({ planChannels, encoderState, outputs }) {
    const encoders = [];
    const byEncoder = new Map();
    for (const ch of planChannels) {
        const encKey = lower(ch.encoder);
        if (!byEncoder.has(encKey)) {
            const enc = { name: norm(ch.encoder), channels: [] };
            byEncoder.set(encKey, enc);
            encoders.push(enc);
        }
        byEncoder.get(encKey).channels.push(ch);
    }

    const out = encoders.map(({ name, channels }) => {
        const state = encoderState[name] || null;
        const statsMap = state && state.stats;
        const statusKnown = !!statsMap && !state.unreachable;
        const sorted = [...channels].sort((a, b) => {
            const na = Number(a.encStreamId), nb = Number(b.encStreamId);
            return !isNaN(na) && !isNaN(nb) ? na - nb : norm(a.encStreamId).localeCompare(norm(b.encStreamId));
        });
        const streams = sorted.map((plan) => {
            const id = norm(plan.encStreamId);
            const key = streamKey(name, id) + '|' + plan.calChannelId;
            const stat = statsMap ? statsMap.get(id) || null : null;
            const running = statusKnown && !!stat && isStreamRunning(stat.status);
            const output = outputs[streamKey(name, id)] || null;
            const expected = { origin: norm(plan.origin), app: norm(plan.streamApp), channel: norm(plan.channelName) };
            let kind;
            let err = null;
            let dest = expected;
            if (!statusKnown) {
                kind = 'unknown';
            } else if (!running) {
                kind = 'down';
            } else if (!output || output.pending || output.failed) {
                kind = 'checking';
            } else if (!output.route) {
                kind = 'nooutput';
                err = 'nooutput';
                dest = null;
            } else if (!same(output.route.origin, expected.origin)) {
                kind = 'mismatch';
                err = 'origin';
                dest = output.route;
            } else if (!same(output.route.channel, expected.channel) || (expected.app && !same(output.route.app, expected.app))) {
                kind = 'mismatch';
                err = 'route';
                dest = output.route;
            } else {
                kind = 'live';
                dest = output.route;
            }
            return {
                key, outputKey: streamKey(name, id), encoder: name, streamId: id,
                name: (stat && stat.streamName) || plan.encStreamName || `Stream ${id}`,
                stat, plan, match: plan.match, running, kind, err, expected, dest, output,
                // Expected destination the stream does not reach
                ghost: !!err && (!dest || !same(dest.origin, expected.origin)) ? expected : null
            };
        });
        return { name, unreachable: !!(state && state.unreachable), pending: !state || !!state.pending, streams };
    });

    const originSet = new Map();
    const addOrigin = o => { if (norm(o) && !originSet.has(lower(o))) originSet.set(lower(o), norm(o)); };
    for (const e of out) for (const s of e.streams) {
        if (s.dest) addOrigin(s.dest.origin);
        if (s.ghost) addOrigin(s.ghost.origin);
    }
    return { encoders: out, origins: [...originSet.values()] };
}

// Does a stream row pass the current focus / errors-only filter?
export function streamMatches(s, { focus, onlyErrors }) {
    if (onlyErrors && !s.err) return false;
    if (focus) {
        const ok = (focus.k === 'enc' && focus.id === s.encoder)
            || (focus.k === 'stream' && focus.id === s.key)
            || (focus.k === 'match' && s.match && focus.id === s.match.id)
            || (focus.k === 'ori' && ((s.dest && same(s.dest.origin, focus.id)) || (s.ghost && same(s.ghost.origin, focus.id))));
        if (!ok) return false;
    }
    return true;
}

export const GEO = { HDR: 60, ROW: 52, OROW: 44, GAP: 24, TOP0: 36, ENC_W: 320, LEAD: 200, CURVE: 160, ORI_W: 300, STUB: 28, OUT_W: 236 };

// Width of everything except the gap the lines curve through.
export const FIXED_W = GEO.ENC_W + GEO.LEAD + GEO.ORI_W + GEO.STUB + GEO.OUT_W;

// Positions of every card, row, pill and line in the map, in px. `gap` is the space between the
// channel labels and the origins (the map component widens it when there is room).
export function layoutRoutes(model, gap = GEO.CURVE) {
    const G = GEO;
    const encR = G.ENC_W;
    const leadX = encR + G.LEAD;
    const oriX = leadX + gap;
    const outX = oriX + G.ORI_W + G.STUB;
    const width = outX + G.OUT_W;
    const midX = Math.round((leadX + oriX) / 2);
    const curve = (sy, ty) => `M ${encR} ${sy} L ${leadX} ${sy} C ${midX} ${sy} ${midX} ${ty} ${oriX} ${ty}`;

    let y = G.TOP0;
    const encoders = model.encoders.map((e) => {
        const top = y;
        let rowY = top + 1 + G.HDR;
        const rows = e.streams.map((s) => {
            const r = { s, top: rowY - top, cy: rowY + G.ROW / 2 };
            rowY += G.ROW;
            return r;
        });
        const h = rowY - top + 8 + 1;
        y += h + G.GAP;
        return { e, top, h, rows };
    });
    const encBottom = y - G.GAP;

    const syByKey = {};
    for (const ec of encoders) for (const r of ec.rows) syByKey[r.s.key] = r.cy;

    const origins = model.origins.map((name) => {
        const items = [];
        for (const e of model.encoders) for (const s of e.streams) {
            const sy = syByKey[s.key];
            if (sy === undefined) continue;
            if (s.dest && lower(s.dest.origin) === lower(name)) items.push({ type: 'in', s, sy });
            if (s.ghost && lower(s.ghost.origin) === lower(name)) items.push({ type: 'exp', s, sy });
        }
        items.sort((a, b) => a.sy - b.sy);
        const avg = items.length ? items.reduce((t, it) => t + it.sy, 0) / items.length : Infinity;
        return { name, items, avg, h: 2 + G.HDR + Math.max(1, items.length) * G.OROW + 8 };
    }).sort((a, b) => a.avg - b.avg);

    let prevBottom = G.TOP0 - G.GAP;
    for (const o of origins) {
        const want = o.items.length ? Math.round(o.avg - o.h / 2) : prevBottom + G.GAP;
        o.top = Math.max(prevBottom + G.GAP, want, G.TOP0);
        prevBottom = o.top + o.h;
        o.items.forEach((it, i) => {
            it.top = 1 + G.HDR + i * G.OROW;
            it.ty = o.top + it.top + G.OROW / 2;
        });
    }

    const lines = [];
    const ghosts = [];
    const pills = [];
    // One origin output box per calendar channel, on the row of the origin the calendar expects
    // (the monitor watches that one) - not on a row where the stream arrives at the wrong origin.
    const outs = [];
    for (const o of origins) for (const it of o.items) {
        if (it.type === 'in') lines.push({ s: it.s, d: curve(it.sy, it.ty) });
        else ghosts.push({ s: it.s, d: curve(it.sy, it.ty) });
        if (!(it.type === 'in' && it.s.err === 'origin')) outs.push({ s: it.s, ty: it.ty });
    }
    for (const e of model.encoders) for (const s of e.streams) {
        const sy = syByKey[s.key];
        if (sy === undefined) continue;
        const ch = s.dest ? s.dest.channel : s.expected.channel;
        pills.push({ s, label: ch, left: encR + 28, top: sy - 13, width: G.LEAD - 44 });
        if (!s.dest) lines.push({ s, d: `M ${encR} ${sy} L ${leadX} ${sy}` });
    }

    return {
        width, height: Math.max(encBottom, prevBottom) + 16,
        oriX, outX, stubX: oriX + G.ORI_W, pillLeft: encR + 28, pillWidth: G.LEAD - 44,
        encoders, origins, lines, ghosts, pills, outs
    };
}

// ---- Origin output (stream monitor, GET /monitor/status) ----

export const MONITOR_STALE_SECONDS = 60;

export function secondsAgo(iso, nowMs) {
    const t = Date.parse(iso);
    return isNaN(t) ? null : Math.max(0, Math.round((nowMs - t) / 1000));
}

export function variantProblem(v) {
    if (!v.healthy) return 'unreachable';
    if (v.is_frozen) return 'frozen';
    if (v.classification === 'black') return 'black screen';
    if (v.classification === 'white') return 'white screen';
    return null;
}

/*
 * One status per calendar channel from its monitor entry: green = every variant OK, amber = at
 * least one variant has a problem, red = all variants have a problem or monitoring could not
 * start, grey = no entry yet, stale data or monitor not reachable.
 */
export function outputStatus(entry, monitor, nowMs) {
    if (monitor.error) return { tone: 'grey', title: 'Monitor not reachable', detail: monitor.error };
    if (!monitor.loaded) return { tone: 'grey', title: 'Loading...', detail: 'Reading the stream monitor' };
    if (!entry) return { tone: 'grey', title: 'Not monitored yet', detail: 'Checks start 2–7 min after the window opens' };
    if (entry.phase === 'error') {
        const notStarted = /master playlist/i.test(entry.error || '');
        return notStarted
            ? { tone: 'red', title: 'Not started', detail: 'Origin has no stream for this channel' }
            : { tone: 'red', title: 'Monitor error', detail: entry.error || 'Unknown error' };
    }
    const ago = secondsAgo(entry.last_cycle_at, nowMs);
    if (ago !== null && ago > MONITOR_STALE_SECONDS) return { tone: 'grey', title: 'Monitor data stale', detail: `Last check ${ago}s ago` };
    const variants = entry.variants || [];
    if (!variants.length) return { tone: 'grey', title: 'Starting checks', detail: 'No variant checked yet' };
    const bad = variants.map(v => ({ name: v.name, p: variantProblem(v) })).filter(x => x.p);
    const n = variants.length;
    if (!bad.length) return { tone: 'green', title: 'Output OK', detail: `${n}/${n} variants` + (ago !== null ? ` · checked ${ago}s ago` : '') };
    if (bad.length === n) {
        const kinds = [...new Set(bad.map(x => x.p))];
        return { tone: 'red', title: 'No usable output', detail: kinds.length === 1 ? `All ${n} variants ${kinds[0]}` : `All ${n} variants failing: ${kinds.join(', ')}` };
    }
    const shown = bad.slice(0, 2).map(x => `${x.name} ${x.p}`).join(' · ');
    return { tone: 'amber', title: 'Output degraded', detail: shown + (bad.length > 2 ? ` · +${bad.length - 2} more` : '') };
}
